import React, { useMemo, useState } from 'react';
import { PencilSquareIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import {
  TABLE_START_LEVEL,
  buildLevel2Grouping,
  buildLocationHierarchy,
  sortedLevel2KeysFromGrouping,
  sortedLocationKeys,
  sortedAssetsFromHierarchy,
} from '../../viewServices/assets_hierarchy';

const formatValue = (v) => {
  if (v === null || v === undefined || v === 0) return '—';
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const sumSnapshotValue = (assets, snapshotMap) =>
  assets.reduce((sum, a) => sum + (snapshotMap?.[a.id]?.snapshot_value ?? 0), 0);

const formatQuantity = (q) => {
  if (q === null || q === undefined) return '—';
  const n = Number(q);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
};

const LoadingSkeleton = ({ rows = 5 }) => (
  <div className="space-y-2 p-3">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="h-7 animate-pulse rounded bg-gray-100" />
    ))}
  </div>
);

const EmptyState = () => (
  <div className="flex items-center justify-center py-8 text-sm text-gray-400">
    No assets registered yet.
  </div>
);

const AssetRow = ({ asset, rowContext, isTopLevel = false }) => {
  const { snapshotMap, onEditAsset, showLocation = true } = rowContext;
  const hasEntry = snapshotMap != null && asset.id in snapshotMap;
  const value = hasEntry ? snapshotMap[asset.id].snapshot_value : null;
  const quantity = hasEntry ? snapshotMap[asset.id].snapshot_quantity : null;
  return (
    <div className={`flex items-center justify-between border-b border-gray-100 px-3 py-2 last:border-0 hover:bg-gray-50 ${isTopLevel ? 'pl-3' : 'pl-14'}`}>
      <div className="flex min-w-0 flex-1 items-center gap-2">
        <button
          type="button"
          onClick={() => onEditAsset?.(asset)}
          className="flex-shrink-0 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          title="Editar"
        >
          <PencilSquareIcon className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 flex-col">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-gray-800">{asset.asset_name}</span>
            {showLocation && asset.location_name && (
              <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-xs text-gray-400">
                {asset.location_name}
              </span>
            )}
            {!asset.is_active && (
              <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs text-red-400">inactive</span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="font-mono text-xs text-gray-400">{asset.asset_code}</span>
          </div>
        </div>
      </div>
      <div className="ml-2 flex flex-shrink-0 flex-col items-end text-right">
        <span className="font-mono text-xs tabular-nums text-gray-600">
          {hasEntry ? `R$ ${formatValue(value)}` : '—'}
        </span>
        <span className="mt-0.5 font-mono text-xs tabular-nums text-gray-400">
          qty: {formatQuantity(quantity)}
        </span>
      </div>
    </div>
  );
};

const Level2Row = ({ level2, assets, rowContext, isTopLevel = false }) => {
  const [open, setOpen] = useState(false);
  const { snapshotMap } = rowContext;
  const total = sumSnapshotValue(assets, snapshotMap);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center justify-between border-b border-gray-100 px-3 py-1.5 transition-colors hover:bg-gray-50 ${isTopLevel ? 'pl-3' : 'pl-8'}`}
      >
        <div className="flex items-center gap-2">
          {open
            ? <ChevronDownIcon className="h-3 w-3 text-gray-400" />
            : <ChevronRightIcon className="h-3 w-3 text-gray-400" />}
          <span className="text-xs font-medium text-gray-500">
            {level2} ({assets.length})
          </span>
        </div>
        <span className="font-mono text-xs tabular-nums text-gray-500">
          {total !== 0 ? `R$ ${formatValue(total)}` : '—'}
        </span>
      </button>
      {open && assets.map((asset) => (
        <AssetRow key={asset.id} asset={asset} rowContext={rowContext} />
      ))}
    </>
  );
};

const Level1Row = ({ level1, level2Map, rowContext }) => {
  const [open, setOpen] = useState(false);
  const { snapshotMap } = rowContext;
  const allAssets = Object.values(level2Map).flat();
  const totalAssets = allAssets.length;
  const total = sumSnapshotValue(allAssets, snapshotMap);
  const sortedLevel2 = Object.keys(level2Map).sort();

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between border-b border-gray-200 bg-gray-50 px-3 py-2 transition-colors hover:bg-gray-100"
      >
        <div className="flex items-center gap-2">
          {open
            ? <ChevronDownIcon className="h-3.5 w-3.5 text-gray-500" />
            : <ChevronRightIcon className="h-3.5 w-3.5 text-gray-500" />}
          <span className="text-sm font-semibold text-gray-700">
            {level1} ({totalAssets})
          </span>
        </div>
        <span className="font-mono text-sm font-semibold tabular-nums text-gray-600">
          {total !== 0 ? `R$ ${formatValue(total)}` : '—'}
        </span>
      </button>
      {open && sortedLevel2.map((l2) => (
        <Level2Row key={l2} level2={l2} assets={level2Map[l2]} rowContext={rowContext} />
      ))}
    </>
  );
};

const AssetsHierarchyBody = ({
  loading,
  filteredAssets,
  hierarchy,
  sortedLevel1,
  snapshotMap,
  tableStartLevel,
  onEditAsset,
  maxHeightClass = 'max-h-[50vh]',
}) => {
  const level2Grouping = useMemo(
    () => buildLevel2Grouping(hierarchy, sortedLevel1),
    [hierarchy, sortedLevel1]
  );
  const sortedLevel2Top = useMemo(
    () => sortedLevel2KeysFromGrouping(level2Grouping),
    [level2Grouping]
  );
  const sortedAssetsLevel3 = useMemo(
    () => sortedAssetsFromHierarchy(hierarchy, sortedLevel1),
    [hierarchy, sortedLevel1]
  );
  const locationHierarchy = useMemo(
    () => buildLocationHierarchy(filteredAssets, hierarchy),
    [filteredAssets, hierarchy]
  );
  const sortedLocations = useMemo(
    () => sortedLocationKeys(locationHierarchy),
    [locationHierarchy]
  );
  const rowContext = useMemo(
    () => ({
      snapshotMap,
      onEditAsset,
      showLocation: tableStartLevel !== TABLE_START_LEVEL.LOCATION,
    }),
    [snapshotMap, onEditAsset, tableStartLevel]
  );

  return (
    <div className={`overflow-auto bg-white ${maxHeightClass}`}>
      {loading && <LoadingSkeleton rows={5} />}
      {!loading && filteredAssets.length === 0 && <EmptyState />}
      {!loading && filteredAssets.length > 0 && (() => {
        if (tableStartLevel === TABLE_START_LEVEL.L1) {
          return sortedLevel1.map((l1) => (
            <Level1Row key={l1} level1={l1} level2Map={hierarchy[l1]} rowContext={rowContext} />
          ));
        }
        if (tableStartLevel === TABLE_START_LEVEL.L2) {
          return sortedLevel2Top.map((l2) => (
            <Level2Row
              key={l2}
              level2={l2}
              assets={level2Grouping[l2]}
              rowContext={rowContext}
              isTopLevel
            />
          ));
        }
        if (tableStartLevel === TABLE_START_LEVEL.LOCATION) {
          return sortedLocations.map((loc) => (
            <Level1Row
              key={loc}
              level1={loc}
              level2Map={locationHierarchy[loc]}
              rowContext={rowContext}
            />
          ));
        }
        return sortedAssetsLevel3.map((asset) => (
          <AssetRow key={asset.id} asset={asset} rowContext={rowContext} isTopLevel />
        ));
      })()}
    </div>
  );
};

export default AssetsHierarchyBody;
